/**
 * 版本化动态组件加载器
 * 基于文件夹扫描自动识别和加载带版本和展示类型的组件
 * 统一结构:
 * - materials/modules/[templateType]/[moduleId]/[version]/index.js (展示组件)
 * - materials/layout/[templateType]/[moduleId]/[version]/index.js (布局组件)
 */
import { defaultVersion } from './modules.js';

// 使用 webpack 的 require.context 动态导入所有版本化组件入口文件
function importAll(r) {
	let modules = {};
	r.keys().forEach(key => {
		modules[key] = r(key);
	});
	return modules;
}
// 动态导入所有版本化的展示组件入口文件
const moduleFiles = importAll(require.context('@rebuild/materials/modules', true, /\/index\.js$/));
// 动态导入所有版本化的布局组件入口文件
const layoutFiles = importAll(require.context('@rebuild/materials/layout', true, /\/index\.js$/));

/**
 * 构建组件入口路径
 * @param {string} templateType - 模板类型
 * @param {string} moduleId - 组件ID
 * @param {string} version - 版本号
 * @returns {string} 组件入口路径
 */
function buildComponentEntryPath(templateType, moduleId, version) {
	// 统一使用 index.js 作为入口文件
	return `./${templateType}/${moduleId}/${version}/index.js`;
}

/**
 * 检查组件是否存在
 * @param {string} templateType - 模板类型
 * @param {string} moduleId - 组件ID
 * @param {string} version - 版本号
 * @param {string} type - 组件类型 ('layout' 或 'display')
 * @returns {boolean} 是否存在
 */
export function hasComponent(templateType, moduleId, version, type = 'display') {
	const entryPath = buildComponentEntryPath(templateType, moduleId, version);
	const files = type === 'layout' ? layoutFiles : moduleFiles;
	return files[entryPath] !== undefined;
}

/**
 * 尝试加载指定版本的组件
 * @param {string} templateType - 模板类型
 * @param {string} moduleId - 组件ID
 * @param {string} version - 版本号
 * @param {string} type - 组件类型 ('layout' 或 'display')
 * @param {string} mode - 展示类型
 * @returns {Object|null} 加载结果 {component, version, mode}
 */
function tryLoadComponentVersion(templateType, moduleId, version, type, mode) {
	if (!hasComponent(templateType, moduleId, version, type)) {
		return null;
	}

	const entryPath = buildComponentEntryPath(templateType, moduleId, version);
	const files = type === 'layout' ? layoutFiles : moduleFiles;
	const mod = files[entryPath];

	const comp = mod?.componentMap?.[mode];
	if (!mod?.componentMap) {
		console.warn(`[没有检索到必要配置(componentMap)] ${templateType}/${moduleId}:${version}`);
	}
	if (comp) {
		return { component: comp };
	}

	return null;
}

/**
 * 动态加载组件
 * @param {Object} params - 加载参数
 * @param {Object} params.meta - 组件元数据
 * @param {string} params.meta.templateType - 模板类型
 * @param {string} params.meta.moduleId - 组件ID
 * @param {string} params.meta.version - 版本号
 * @param {string} params.meta.type - 组件类型 ('layout' 或 'display')
 * @param {string} params.mode - 展示类型
 * @returns {Object|null} 加载结果 {component, version, mode}
 */
export function loadComponent({ meta, mode }) {
	const { templateType, moduleId, version, type = 'display' } = meta;

	if (!mode || !templateType || !moduleId) return null;

	// 尝试加载目标版本
	if (version) {
		const result = tryLoadComponentVersion(templateType, moduleId, version, type, mode);
		if (result) return result;
	}

	// 尝试加载标准版本
	if (defaultVersion) {
		console.warn(
			`组件 ${templateType}/${moduleId}:${version} (${
				type === 'layout' ? 'layout' : mode
			}) 不存在，降级到标准版本 ${defaultVersion}`
		);

		const result = tryLoadComponentVersion(templateType, moduleId, defaultVersion, type, mode);
		if (result) return result;
	}

	// 所有版本都不存在
	return null;
}
